import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function OrBike365() {
  const fordele = [
    {
      icon: "fa-ticket",
      titel: "Tidlig adgang til billetsalg",
      beskrivelse: "Få adgang til billetsalget før alle andre og vær sikker på din cykel i Ørbæk Midtpunkt."
    },
    {
      icon: "fa-tags",
      titel: "Rabat på din billet",
      beskrivelse: "Særlig medlemsrabat på din personlige billet eller cykelreservation til ØrBike eventet."
    },
    {
      icon: "fa-plane-departure",
      titel: "Særlige tilbud & sportsrejser",
      beskrivelse: "Få adgang til eksklusive tilbud, sportsrejser, træningsevents og unikke cykeloplevelser."
    },
    {
      icon: "fa-sparkles",
      titel: "Nye ØrBike 365-tiltag",
      beskrivelse: "Eksklusiv adgang til nye arrangementer og koncepter, som vi allerede er i fuld gang med at udvikle."
    },
    {
      icon: "fa-heart-pulse",
      titel: "Støt det gode formål & fællesskabet",
      beskrivelse: "Du er med til at bakke op om foreningen og det unikke fællesskab, vi skaber sammen for børn med kritisk sygdom."
    }
  ];

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>ØrBike 365 – Medlemskab & Fællesskab Hele Året</title>
        <meta
          name="description"
          content="Bliv en del af ØrBike 365. Fællesskabet stopper ikke, når cyklerne står stille. Få adgang før andre, rabat på billetter, sportsrejser og meget mere."
        />
        <meta property="og:title" content="ØrBike 365 – Medlemskab & Fællesskab Hele Året" />
        <meta property="og:description" content="Med ØrBike 365 kommer du lidt tættere på – hele året. Læs om fordelene og tilkendegiv din interesse allerede nu." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-42 pb-16 sm:pb-24 bg-gradient-to-b from-[#18112e] via-[#101221] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-pink-600/15 to-purple-600/15 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30">
            <i className="fa-solid fa-star text-[11px]" aria-hidden="true"></i>
            Medlemskab & Fællesskab hele året
          </span>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black font-display text-white uppercase tracking-tight">
            ØrBike <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">365</span>
          </h1>

          <p className="text-pink-300 font-display text-xl sm:text-3xl font-bold uppercase">
            Fællesskabet stopper ikke, når cyklerne står stille.
          </p>

          <div className="max-w-2xl mx-auto text-slate-300 text-base sm:text-lg leading-relaxed space-y-3">
            <p>
              ØrBike er mere end én dag om året. Det er energi, oplevelser og et fællesskab, der samtidig er med til at gøre en forskel.
            </p>
            <p className="font-semibold text-white">
              Med ØrBike 365 kommer du lidt tættere på – hele året.
            </p>
          </div>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt?emne=ØrBike 365"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-pink-500 via-purple-600 to-pink-500 text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_25px_rgba(255,42,141,0.5)] hover:shadow-[0_0_35px_rgba(255,42,141,0.8)] transition-all hover:scale-105"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true"></i>
              <span>Tilkendegiv interesse</span>
            </Link>
            <Link
              to="/eventet"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-sm uppercase tracking-wider transition-all"
            >
              <span>Se ØrBike Eventet</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Fordele Sektion */}
      <section className="py-16 sm:py-20 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
            Eksklusive Fordele
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight">
            Som en del af ØrBike 365 får du blandt andet:
          </h2>
          <p className="text-slate-400 text-base">
            Vi skaber et medlemskab fyldt med merværdi for dig, der elsker cykling, stemning og velgørenhed.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fordele.map((fordel, index) => (
            <div
              key={index}
              className="bg-[#12141f] border border-slate-800 hover:border-pink-500/40 rounded-3xl p-8 transition-all hover:-translate-y-1 group shadow-lg flex flex-col justify-between"
            >
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30 text-pink-400 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                  <i className={`fa-solid ${fordel.icon}`} aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold text-white font-display uppercase tracking-tight">
                  {fordel.titel}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {fordel.beskrivelse}
                </p>
              </div>

              <div className="pt-6 mt-4 border-t border-slate-800/80 flex items-center gap-2 text-xs font-semibold text-pink-400">
                <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                <span>Inkluderet i ØrBike 365</span>
              </div>
            </div>
          ))}

          {/* Special kort: Billedstemning / Vision */}
          <div className="relative rounded-3xl overflow-hidden border border-pink-500/30 group shadow-lg min-h-[260px] flex flex-col justify-end p-8 bg-slate-900">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9995.jpg"
              alt="ØrBike Stemning"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0f1a] via-[#0d0f1a]/60 to-transparent"></div>
            <div className="relative z-10 space-y-2">
              <span className="text-xs font-mono uppercase tracking-widest text-pink-400 font-bold">
                10 timers cykelfest & energi
              </span>
              <h4 className="text-xl font-black text-white font-display uppercase">
                Et fællesskab der rækker ud over dagen
              </h4>
              <p className="text-xs text-slate-300">
                Sammen med 1.000+ deltagere gør vi en forskel, der kan mærkes året rundt.
              </p>
            </div>
          </div>
        </div>

        {/* OBS Boks */}
        <div className="p-6 sm:p-8 rounded-3xl bg-[#12141f] border border-pink-500/40 flex flex-col md:flex-row items-center gap-6 shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-pink-500/20 border border-pink-500/40 text-pink-400 flex items-center justify-center text-2xl shrink-0">
            <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
          </div>
          <div className="space-y-1 text-center md:text-left flex-1">
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
              Lancering & Tidsplan
            </span>
            <h3 className="text-xl sm:text-2xl font-black text-white font-display uppercase">
              Vigtig information om ØrBike 365
            </h3>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              <strong>OBS!</strong> Der kommer mere information om ØrBike 365 i starten af <strong>2027</strong>, og vi glæder os til at løfte sløret for pris, fordele mv.
            </p>
          </div>
          <div className="shrink-0">
            <span className="inline-block px-4 py-2 rounded-xl bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold uppercase tracking-wider font-mono">
              Kommer primo 2027
            </span>
          </div>
        </div>

        {/* Tilkendegiv Interesse CTA Boks */}
        <div className="relative rounded-3xl overflow-hidden border border-pink-500/40 bg-gradient-to-r from-pink-950/50 via-purple-950/50 to-slate-900 p-8 sm:p-14 shadow-2xl text-center space-y-6">
          <div className="max-w-2xl mx-auto space-y-4">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30">
              Tidlig Tilkendegivelse
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight">
              Skal du med i ØrBike 365?
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Så udfyld vores kontaktformular og vælg <strong>“ØrBike 365”</strong> og tilkendegiv allerede nu din interesse.
            </p>
            <p className="text-pink-300 font-display font-semibold text-base sm:text-lg">
              Vi glæder os til at have dig med – ikke bare til ØrBike, men hele året rundt.
            </p>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt?emne=ØrBike 365"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-pink-500 via-purple-600 to-pink-500 text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_25px_rgba(255,42,141,0.5)] hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
              <span>Tilkendegiv din interesse for ØrBike 365</span>
            </Link>

            <Link
              to="/om-os"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-sm uppercase tracking-wider transition-all"
            >
              <i className="fa-solid fa-users" aria-hidden="true"></i>
              <span>Læs om Foreningen ØrBike</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
